import {
    type MouseEvent,
    type MutableRefObject,
    type FC,
    type ReactNode,
    type CSSProperties,
    useMemo,
} from 'react';
import classnames from 'classnames';
import { useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { type ButtonStyles, type CustomButtonColors } from '../../Button/index.tsx';
import { colors } from 'dibs-sassy/exports/colors.ts';
// styles
import styles from './main.scss';
type ButtonType = 'button';
type ResetType = 'reset';
type SubmitType = 'submit';

export type ButtonTypesType = ButtonType | ResetType | SubmitType | undefined;

type StyledButtonProps = {
    className?: string;
    children?: ReactNode;
    dataTn?: string;
    disabled?: boolean;
    href?: string;
    isPrimary?: boolean;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    rel?: string;
    tabIndex?: number;
    target?: string;
    type?: ButtonTypesType;
    value?: string;
    ariaLabel?: string;
    buttonRef?:
        | ((element: HTMLElement | null) => void)
        | MutableRefObject<HTMLButtonElement | HTMLAnchorElement | null>;
    form?: string;
    roundedCorners?: boolean;
    style?: ButtonStyles;
    cssStyle?: CSSProperties;
    customColors?: CustomButtonColors;
};

export const StyledButton: FC<StyledButtonProps> = props => {
    const {
        className,
        children,
        dataTn,
        disabled,
        href,
        isPrimary = false,
        onClick,
        rel,
        tabIndex,
        target,
        type,
        value,
        ariaLabel,
        buttonRef,
        form,
        roundedCorners,
        style,
        cssStyle,
        customColors,
    } = props;
    const hasCustomColors = !!customColors && Object.values(customColors).some(Boolean);

    const styledButtonClasses = classnames(
        styles.button,
        {
            [styles.primaryButton]: isPrimary,
            [styles.disabled]: disabled,
            [styles.roundedCorners]: roundedCorners,
            ...(hasCustomColors && {
                [styles.customColors]: true,
                [styles.customColorsBackground]: !!customColors.background,
                [styles.customColorsHoverBackground]: !!customColors.hoverBackground,
                [styles.customColorsText]: !!customColors.text,
                [styles.customColorsHoverText]: !!customColors.hoverText,
                [styles.customColorsBorder]: !!customColors.border,
            }),
        },
        style && styles[style],
        className
    );

    const cssStyles = useMemo(() => {
        if (!customColors) {
            return undefined;
        }

        return {
            ...(customColors?.background && {
                ['--btn-custom-bg-color']: colors[customColors.background],
            }),
            ...(customColors?.hoverBackground && {
                ['--btn-custom-hover-bg-color']: colors[customColors.hoverBackground],
            }),
            ...(customColors?.text && {
                ['--btn-custom-text-color']: colors[customColors.text],
            }),
            ...(customColors?.hoverText && {
                ['--btn-custom-hover-text-color']: colors[customColors.hoverText],
            }),
            ...(customColors?.border && {
                ['--btn-custom-border-color']: colors[customColors.border],
            }),
        } as CSSProperties;
    }, [customColors]);

    const extraProps = {
        disabled,
        onClick,
        rel,
        tabIndex,
        target,
        value,
        style: { ...cssStyle, ...cssStyles },
    };
    const intl = useIntl();
    if (typeof href === 'string') {
        return (
            // this is the base element so this is allowed here
            // eslint-disable-next-line react/forbid-elements
            <a
                ref={
                    buttonRef as
                        | ((element: HTMLElement | null) => void)
                        | MutableRefObject<HTMLAnchorElement>
                }
                aria-label={ariaLabel}
                className={styledButtonClasses}
                data-tn={dataTn}
                href={handleLocaleUrl(disabled ? undefined : href, intl.locale)}
                {...extraProps}
            >
                {children}
            </a>
        );
    } else {
        return (
            // this is the base element so this is allowed here
            // eslint-disable-next-line react/forbid-elements
            <button
                ref={
                    buttonRef as
                        | ((element: HTMLElement | null) => void)
                        | MutableRefObject<HTMLButtonElement>
                }
                aria-label={ariaLabel}
                className={styledButtonClasses}
                data-tn={dataTn}
                // might want to revisit this logic to add a condition
                // to ensure `type` is not undefined before rendering a button
                // eslint-disable-next-line react/button-has-type
                type={type}
                form={form}
                {...extraProps}
            >
                {children}
            </button>
        );
    }
};
